// The task thread's STOP action (owner, 11 Aug 2026: "Add the stop button to tasks"). The general chat dock
// has carried one since the run-control restore; a task run could not be stopped from the screen at all.
// It routes to the SAME human-only run interrupt the dock uses — `api.interruptRun` → `agent.interruptAgentRun`,
// which refuses the agent Actor in the owning module (G-054) — so who may stop a run is NOT re-decided here:
// a task-scoped route would have been a second answer to that question. Split out of `useTaskThread` for that
// file's size ceiling.

import { useCallback, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { emptyChatStream } from './chat-turns.ts';
import type { ChatStreamState } from './chat-turns.ts';
import { useSurfaceApi } from './SurfaceApiContext.tsx';

/** Honest fallback when the transport refuses without its own sanitised note (never a reason code — GI-07). */
const REFUSED_STOP_NOTE = 'Ritona could not be stopped on this task.';

interface MutableValue<T> {
  current: T;
}

export interface PreviewTeardown {
  /** The fence a terminal reconcile uses: it holds the run id until a snapshot stops calling it running. */
  readonly fence: MutableValue<string | null>;
  readonly generation: MutableValue<number>;
  readonly stop: MutableValue<(() => void) | null>;
  readonly activeRun: MutableValue<string | null>;
  readonly setPreview: Dispatch<SetStateAction<ChatStreamState>>;
}

/** Close the live task stream on a human Stop. The run is FENCED exactly as a terminal reconcile fences it:
 *  the Task snapshot still says `running` until the reload lands, and without the fence the auto-attach
 *  effect would immediately re-open the stream this just tore down. */
export function useClosePreview({
  fence,
  generation,
  stop,
  activeRun,
  setPreview,
}: PreviewTeardown): (runId: string) => void {
  return useCallback(
    (runId: string): void => {
      fence.current = runId;
      generation.current += 1;
      stop.current?.();
      stop.current = null;
      activeRun.current = null;
      setPreview(emptyChatStream());
    },
    [activeRun, fence, generation, setPreview, stop],
  );
}

export interface TaskInterruptInput {
  /** The run a Stop targets: the live preview's run, else the newest running run in the Task snapshot. */
  readonly activeRunId: string | null;
  /** Fence + tear down the live preview, so only the refreshed snapshot reports what happened to the run. */
  readonly closePreview: (runId: string) => void;
  readonly reload: () => void;
  readonly setNote: (value: string | null) => void;
  readonly mounted: MutableValue<boolean>;
}

/** Stop this task's live run, then let the authoritative Task read surface the `interrupted` state — the
 *  screen never sets it (N-CHAT-2 AC-1). A refusal keeps the thread as it was and says so. */
export function useTaskInterrupt({
  activeRunId,
  closePreview,
  reload,
  setNote,
  mounted,
}: TaskInterruptInput): () => Promise<void> {
  const api = useSurfaceApi();
  return useCallback(async (): Promise<void> => {
    if (activeRunId === null) return;
    const result = await api.interruptRun(activeRunId);
    if (!mounted.current) return;
    if (!result.ok) {
      setNote(result.note ?? REFUSED_STOP_NOTE);
      return;
    }
    closePreview(activeRunId);
    reload();
  }, [activeRunId, api, closePreview, mounted, reload, setNote]);
}

export interface TaskContinueInput {
  readonly openPreview: (runId: string) => void;
  readonly setNote: (value: string | null) => void;
  readonly mounted: MutableValue<boolean>;
}

/** Resume one inactivity-paused Task run through the same server-owned Continue operation as private chat. */
export function useTaskContinue({ openPreview, setNote, mounted }: TaskContinueInput): {
  readonly continuingRunId: string | null;
  readonly continueRun: (runId: string) => Promise<void>;
} {
  const api = useSurfaceApi();
  const [continuingRunId, setContinuingRunId] = useState<string | null>(null);
  const continueRun = useCallback(
    async (runId: string): Promise<void> => {
      if (continuingRunId !== null) return;
      setContinuingRunId(runId);
      setNote(null);
      try {
        const result = await api.continueRun(runId);
        if (!mounted.current) return;
        if (!result.ok) {
          setNote(result.note);
          return;
        }
        openPreview(result.runId);
      } catch {
        if (mounted.current) setNote('Ritona could not resume this task run.');
      } finally {
        if (mounted.current) setContinuingRunId(null);
      }
    },
    [api, continuingRunId, mounted, openPreview, setNote],
  );
  return { continuingRunId, continueRun };
}
